/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Card, Input, Table, Typography, Spin, message } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import constants from "../../../constants/constants";
import { formatIndianNumber, formatIndianCurrency } from "../../../utils";
import { DirectorDashboardService } from "../../../services/api/directorDashboardService";
import "./style.scss";
import moment from "moment";

const { Title } = Typography;
const { ROLES } = constants;
const { ROLE_MD } = ROLES;

const DirectorDashboard2 = () => {
    const navigate = useNavigate();
    const [clientSearch, setClientSearch] = useState("");
    const [userRoles, setUserRoles] = useState([]);
    
    // Individual loading states for each section
    const [kpiDataLoading, setKpiDataLoading] = useState(false);
    const [businessDataLoading, setBusinessDataLoading] = useState(false);
    const [shipmentCountsLoading, setShipmentCountsLoading] = useState(false);
    const [deliveryDataLoading, setDeliveryDataLoading] = useState(false);

    // State for Today Hub Operations
    const [kpiData, setKpiData] = useState({
        todayHubInscanShipments: 0,
        todayHubInscanBags: 0,
        todayHubOutscanShipments: 0,
        todayHubOutscanBags: 0,
        todayHubFinalizeBags: 0,
    });

    // State for Business Data
    const [businessDataToday, setBusinessDataToday] = useState([]);
    const [businessDataRange, setBusinessDataRange] = useState([]);
    const [businessDataTodayTimeZone, setBusinessDataTodayTimeZone] = useState("");
    const [businessDataRangeTimeZone, setBusinessDataRangeTimeZone] = useState("");

    // State for Shipment Counts
    const [shipmentCountsToday, setShipmentCountsToday] = useState([]);
    const [shipmentCountsRange, setShipmentCountsRange] = useState([]);
    const [shipmentCountsTodayTimeZone, setShipmentCountsTodayTimeZone] = useState("");
    const [shipmentCountsRangeTimeZone, setShipmentCountsRangeTimeZone] = useState("");

    // State for Delivery Data
    const [deliveryDataToday, setDeliveryDataToday] = useState([]);
    const [deliveryDataRange, setDeliveryDataRange] = useState([]);
    const [deliveryDataTodayTimeZone, setDeliveryDataTodayTimeZone] = useState("");

    useEffect(() => {
        const userinfo = JSON.parse(localStorage.getItem("userinfo") || "{}");
        setUserRoles(userinfo?.roles || []);
    }, []);

    // Helper function to parse and format numbers
    const parseAndFormat = useCallback((value) => {
        if (!value) return "0";
        const numValue = parseInt(value.toString().replace(/,/g, "").trim(), 10);
        return isNaN(numValue) ? "0" : formatIndianNumber(numValue);
    }, []);

    // Fetch all dashboard data - each API updates UI as soon as response arrives
    const fetchDashboardData = useCallback(async () => {
        // Set loading states for each section
        setKpiDataLoading(true);
        setBusinessDataLoading(true);
        setShipmentCountsLoading(true);
        setDeliveryDataLoading(true);

        // Fetch HubOps data - update immediately when response arrives
        DirectorDashboardService.getHubOpsData()
            .then((hubOpsResponse) => {
                if (hubOpsResponse?.data) {
                    const data = hubOpsResponse.data;
                    setKpiData({
                        todayHubInscanShipments: data.inscanShipmentCount?.value || 0,
                        todayHubInscanBags: data.inscanBagCount?.value || 0,
                        todayHubOutscanShipments: data.outscanShipmentCount?.value || 0,
                        todayHubOutscanBags: data.outscanBagCount?.value || 0,
                        todayHubFinalizeBags: data.finalizedBagCount?.value || 0,
                    });
                }
            })
            .catch((error) => {
                console.error("Error fetching HubOps data:", error);
            })
            .finally(() => {
                setKpiDataLoading(false);
            });

        // Fetch Shipment Business Data - update immediately when response arrives
        DirectorDashboardService.getShipmentBusinessData()
            .then((shipmentBusinessResponse) => {
                if (shipmentBusinessResponse?.data) {
                    const data = shipmentBusinessResponse.data;

                    // Business Data - Previous Day
                    if (data.businessDataPreviousDay) {
                        const businessToday = data.businessDataPreviousDay;
                        setBusinessDataTodayTimeZone(businessToday.TimeZone || "");
                        setBusinessDataToday([
                            { key: "1", label: "Cash", value: `₹ ${businessToday.CollectionFigureCash || "0.00"}` },
                            { key: "2", label: "Account", value: `₹ ${businessToday.CollectionFigureAccount || "0.00"}` },
                            { key: "3", label: "DP", value: `₹ ${businessToday.DP || "0.00"}` },
                            { key: "4", label: "Piece Rate", value: `₹ ${businessToday.PieceRate || "0.00"}` },
                            { key: "5", label: "FSC Charge", value: `₹ ${businessToday.FSC || "0.00"}` },
                            { key: "6", label: "Total", value: `₹ ${businessToday.CollectionFigureWithFSC || "0.00"}` },
                        ]);
                    }

                    // Business Data - Current Month
                    if (data.businessDataCurrentMonth) {
                        const businessRange = data.businessDataCurrentMonth;
                        setBusinessDataRangeTimeZone(businessRange.TimeZone || "");
                        setBusinessDataRange([
                            { key: "1", label: "Cash", value: `₹ ${businessRange.CollectionFigureCash || "0.00"}` },
                            { key: "2", label: "Account", value: `₹ ${businessRange.CollectionFigureAccount || "0.00"}` },
                            { key: "3", label: "DP", value: `₹ ${businessRange.DP || "0.00"}` },
                            { key: "4", label: "Piece Rate", value: `₹ ${businessRange.PieceRate || "0.00"}` },
                            { key: "5", label: "FSC Charge", value: `₹ ${businessRange.FSC || "0.00"}` },
                            { key: "6", label: "Total", value: `₹ ${businessRange.CollectionFigureWithFSC || "0.00"}` },
                        ]);
                    }

                    // Shipment Counts - Previous Day
                    if (data.shipmentCountPreviousDay) {
                        const shipmentToday = data.shipmentCountPreviousDay;
                        setShipmentCountsTodayTimeZone(shipmentToday.TimeZone || "");
                        setShipmentCountsToday([
                            { key: "1", label: "Cash Count", value: parseAndFormat(shipmentToday.TotalCashBookingCount) },
                            { key: "2", label: "Account Count", value: parseAndFormat(shipmentToday.TotalAccountBookingCount) },
                            { key: "3", label: "DP Counts", value: parseAndFormat(shipmentToday.TotalDP) },
                            { key: "4", label: "Piece Rate Count", value: parseAndFormat(shipmentToday.TotalPieceRate) },
                            { key: "5", label: "Total", value: parseAndFormat(shipmentToday.TotalBookingCount) },
                        ]);
                    }

                    // Shipment Counts - Current Month
                    if (data.shipmentCountCurrentMonth) {
                        const shipmentRange = data.shipmentCountCurrentMonth;
                        setShipmentCountsRangeTimeZone(shipmentRange.TimeZone || "");
                        setShipmentCountsRange([
                            { key: "1", label: "Cash Count", value: parseAndFormat(shipmentRange.TotalCashBookingCount) },
                            { key: "2", label: "Account Count", value: parseAndFormat(shipmentRange.TotalAccountBookingCount) },
                            { key: "3", label: "DP Counts", value: parseAndFormat(shipmentRange.TotalDP) },
                            { key: "4", label: "Piece Rate Count", value: parseAndFormat(shipmentRange.TotalPieceRate) },
                            { key: "5", label: "Total", value: parseAndFormat(shipmentRange.TotalBookingCount) },
                        ]);
                    }
                }
            })
            .catch((error) => {
                console.error("Error fetching Shipment Business Data:", error);
            })
            .finally(() => {
                // Hide loading for both Business Data and Shipment Counts when this API completes
                setBusinessDataLoading(false);
                setShipmentCountsLoading(false);
            });

        // Fetch Delivery Data - update immediately when response arrives
        DirectorDashboardService.getDeliveryData()
            .then((deliveryResponse) => {
                if (deliveryResponse?.data) {
                    const data = deliveryResponse.data;
                    if (data.deliveryPreviousDay) {
                        const deliveryToday = data.deliveryPreviousDay;
                        setDeliveryDataTodayTimeZone(deliveryToday.TimeZone || "");
                        setDeliveryDataToday([
                            { key: "1", label: "Delivery Percentage", value: `${deliveryToday.TotalDeliveredPerc || "0"}%` },
                            { key: "2", label: "Total DRS", value: parseAndFormat(deliveryToday.TotalDrs) },
                            { key: "3", label: "Delivered Shipments", value: parseAndFormat(deliveryToday.TotalDeliveredDocs) },
                            { key: "4", label: "Return Shipments", value: parseAndFormat(deliveryToday.TotalReturnDocs) },
                            { key: "5", label: "CNFD Shipments", value: parseAndFormat(deliveryToday.TotalCFNDDocs) },
                            { key: "6", label: "Pending Shipments", value: parseAndFormat(deliveryToday.TotalPendingDocs) },
                            { key: "7", label: "Return Delivery", value: parseAndFormat(deliveryToday.TotalReturnDeliveredDocs) },
                            { key: "8", label: "RTO Delivery", value: parseAndFormat(deliveryToday.TotalRTODeliveredDocs) },
                            { key: "9", label: "Total Shipments", value: parseAndFormat(deliveryToday.TotalDrsDocs) },
                        ]);
                    }
                }
            })
            .catch((error) => {
                console.error("Error fetching Delivery Data:", error);
            })
            .finally(() => {
                setDeliveryDataLoading(false);
            });
    }, [parseAndFormat]);

    useEffect(() => {
        if (userRoles.includes(ROLE_MD)) {
            fetchDashboardData();
        }
    }, [userRoles, fetchDashboardData]);

    // Refresh HubOps data (KPI cards)
    const handleRefreshHubOps = useCallback(() => {
        setKpiDataLoading(true);
        DirectorDashboardService.getHubOpsData()
            .then((hubOpsResponse) => {
                if (hubOpsResponse?.data) {
                    const data = hubOpsResponse.data;
                    setKpiData({
                        todayHubInscanShipments: data.inscanShipmentCount?.value || 0,
                        todayHubInscanBags: data.inscanBagCount?.value || 0,
                        todayHubOutscanShipments: data.outscanShipmentCount?.value || 0,
                        todayHubOutscanBags: data.outscanBagCount?.value || 0,
                        todayHubFinalizeBags: data.finalizedBagCount?.value || 0,
                    });
                }
            })
            .catch((error) => {
                console.error("Error fetching HubOps data:", error);
            })
            .finally(() => {
                setKpiDataLoading(false);
            });
    }, []);

    // Refresh Business Data and Shipment Counts (from same API)
    const handleRefreshBusinessData = useCallback(() => {
        setBusinessDataLoading(true);
        setShipmentCountsLoading(true);

        DirectorDashboardService.getShipmentBusinessData()
            .then((shipmentBusinessResponse) => {
                if (shipmentBusinessResponse?.data) {
                    const data = shipmentBusinessResponse.data;

                    // Business Data - Previous Day
                    if (data.businessDataPreviousDay) {
                        const businessToday = data.businessDataPreviousDay;
                        setBusinessDataTodayTimeZone(businessToday.TimeZone || "");
                        setBusinessDataToday([
                            { key: "1", label: "Cash", value: `₹ ${businessToday.CollectionFigureCash || "0.00"}` },
                            { key: "2", label: "Account", value: `₹ ${businessToday.CollectionFigureAccount || "0.00"}` },
                            { key: "3", label: "DP", value: `₹ ${businessToday.DP || "0.00"}` },
                            { key: "4", label: "Piece Rate", value: `₹ ${businessToday.PieceRate || "0.00"}` },
                            { key: "5", label: "FSC Charge", value: `₹ ${businessToday.FSC || "0.00"}` },
                            { key: "6", label: "Total", value: `₹ ${businessToday.CollectionFigureWithFSC || "0.00"}` },
                        ]);
                    }

                    // Business Data - Current Month
                    if (data.businessDataCurrentMonth) {
                        const businessRange = data.businessDataCurrentMonth;
                        setBusinessDataRangeTimeZone(businessRange.TimeZone || "");
                        setBusinessDataRange([
                            { key: "1", label: "Cash", value: `₹ ${businessRange.CollectionFigureCash || "0.00"}` },
                            { key: "2", label: "Account", value: `₹ ${businessRange.CollectionFigureAccount || "0.00"}` },
                            { key: "3", label: "DP", value: `₹ ${businessRange.DP || "0.00"}` },
                            { key: "4", label: "Piece Rate", value: `₹ ${businessRange.PieceRate || "0.00"}` },
                            { key: "5", label: "FSC Charge", value: `₹ ${businessRange.FSC || "0.00"}` },
                            { key: "6", label: "Total", value: `₹ ${businessRange.CollectionFigureWithFSC || "0.00"}` },
                        ]);
                    }

                    // Shipment Counts - Previous Day
                    if (data.shipmentCountPreviousDay) {
                        const shipmentToday = data.shipmentCountPreviousDay;
                        setShipmentCountsTodayTimeZone(shipmentToday.TimeZone || "");
                        setShipmentCountsToday([
                            { key: "1", label: "Cash Count", value: parseAndFormat(shipmentToday.TotalCashBookingCount) },
                            { key: "2", label: "Account Count", value: parseAndFormat(shipmentToday.TotalAccountBookingCount) },
                            { key: "3", label: "DP Counts", value: parseAndFormat(shipmentToday.TotalDP) },
                            { key: "4", label: "Piece Rate Count", value: parseAndFormat(shipmentToday.TotalPieceRate) },
                            { key: "5", label: "Total", value: parseAndFormat(shipmentToday.TotalBookingCount) },
                        ]);
                    }

                    // Shipment Counts - Current Month
                    if (data.shipmentCountCurrentMonth) {
                        const shipmentRange = data.shipmentCountCurrentMonth;
                        setShipmentCountsRangeTimeZone(shipmentRange.TimeZone || "");
                        setShipmentCountsRange([
                            { key: "1", label: "Cash Count", value: parseAndFormat(shipmentRange.TotalCashBookingCount) },
                            { key: "2", label: "Account Count", value: parseAndFormat(shipmentRange.TotalAccountBookingCount) },
                            { key: "3", label: "DP Counts", value: parseAndFormat(shipmentRange.TotalDP) },
                            { key: "4", label: "Piece Rate Count", value: parseAndFormat(shipmentRange.TotalPieceRate) },
                            { key: "5", label: "Total", value: parseAndFormat(shipmentRange.TotalBookingCount) },
                        ]);
                    }
                }
            })
            .catch((error) => {
                console.error("Error fetching Shipment Business Data:", error);
            })
            .finally(() => {
                setBusinessDataLoading(false);
                setShipmentCountsLoading(false);
            });
    }, [parseAndFormat]);

    // Refresh Shipment Counts (same API as Business Data, but separate handler for clarity)
    const handleRefreshShipmentCounts = useCallback(() => {
        handleRefreshBusinessData();
    }, [handleRefreshBusinessData]);

    // Refresh Delivery Data
    const handleRefreshDeliveryData = useCallback(() => {
        setDeliveryDataLoading(true);

        DirectorDashboardService.getDeliveryData()
            .then((deliveryResponse) => {
                if (deliveryResponse?.data) {
                    const data = deliveryResponse.data;
                    if (data.deliveryPreviousDay) {
                        const deliveryToday = data.deliveryPreviousDay;
                        setDeliveryDataTodayTimeZone(deliveryToday.TimeZone || "");
                        setDeliveryDataToday([
                            { key: "1", label: "Delivery Percentage", value: `${deliveryToday.TotalDeliveredPerc || "0"}%` },
                            { key: "2", label: "Total DRS", value: parseAndFormat(deliveryToday.TotalDrs) },
                            { key: "3", label: "Delivered Shipments", value: parseAndFormat(deliveryToday.TotalDeliveredDocs) },
                            { key: "4", label: "Return Shipments", value: parseAndFormat(deliveryToday.TotalReturnDocs) },
                            { key: "5", label: "CNFD Shipments", value: parseAndFormat(deliveryToday.TotalCFNDDocs) },
                            { key: "6", label: "Pending Shipments", value: parseAndFormat(deliveryToday.TotalPendingDocs) },
                            { key: "7", label: "Return Delivery", value: parseAndFormat(deliveryToday.TotalReturnDeliveredDocs) },
                            { key: "8", label: "RTO Delivery", value: parseAndFormat(deliveryToday.TotalRTODeliveredDocs) },
                            { key: "9", label: "Total Shipments", value: parseAndFormat(deliveryToday.TotalDrsDocs) },
                        ]);
                    }
                }
            })
            .catch((error) => {
                console.error("Error fetching Delivery Data:", error);
            })
            .finally(() => {
                setDeliveryDataLoading(false);
            });
    }, [parseAndFormat]);

    // Refresh all data (for initial load)
    const handleRefresh = () => {
        fetchDashboardData();
    };

    const handleViewZoneWise = (e, type = "today", reportType = "Piece Rate", reportCategory = "Business Data", businessType = null) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        const dateToPass = type === "today" ? moment() : moment().startOf("month");
        const routeMap = {
            "Business Data": "/zone-wise-business-data",
            "Shipment Counts": "/zone-wise-shipment-counts",
            "Delivery Data": "/zone-wise-delivery-data",
            
        };
        navigate(routeMap[reportCategory] || "/zone-wise-business-data", {
            state: {
                type,
                reportType,
                reportCategory,
                date: dateToPass.format("YYYY-MM-DD"), // Convert moment to string
                businessType, // Pass selected business type
            },
        });
    };
    
    // Handle click on table row (Cash, Account, DP, Piece Rate, etc.)
    const handleTableRowClick = (record, reportCategory, dataType) => {
        // Don't navigate if it's the Total row
        if (record.label === "Total") {
            return;
        }
        
        // Map label to business type name - use label directly as businessType
        const businessType = record.label;
        
        // Navigate to ZoneList route with selected business type
        const dateToPass = dataType === "today" ? moment() : moment().startOf("month");
        navigate("/zone-list", {
            state: {
                type: dataType,
                reportType: "Piece Rate",
                reportCategory: reportCategory,
                date: dateToPass.format("YYYY-MM-DD"),
                businessType: businessType,
            },
        });
    };

    // Check if user has ROLE_MD access
    if (!userRoles.includes(ROLE_MD)) {
        return (
            <div className="director-dashboard-access-denied">
                <Card>
                    <Title level={4}>Access Denied</Title>
                    <p>You do not have permission to access this dashboard.</p>
                </Card>
            </div>
        );
    }

    // Use TimeZone from API if available, otherwise use current date
    const todayDate = businessDataTodayTimeZone || moment().format("DD/MM/YYYY");
    const dateRange = businessDataRangeTimeZone || (() => {
        const rangeStartDate = moment().startOf("month").format("DD/MM/YYYY");
        const rangeEndDate = moment().format("DD/MM/YYYY");
        return `${rangeStartDate} - ${rangeEndDate}`;
    })();
    
    // Use TimeZone from API for Shipment Counts if available
    const shipmentTodayDate = shipmentCountsTodayTimeZone || todayDate;
    const shipmentDateRange = shipmentCountsRangeTimeZone || dateRange;

    // Table columns for data tables
    const dataTableColumns = [
        {
            title: "",
            dataIndex: "label",
            key: "label",
            width: "60%",
        },
        {
            title: "",
            dataIndex: "value",
            key: "value",
            width: "40%",
            align: "right",
        },
    ];

    // Helper function to create columns with clickable label for Previous Day tables
    const getClickableLabelColumns = (reportCategory, dataType) => {
        // Define which labels should be clickable based on report category
        const clickableLabels = {
            "Business Data": ["Cash", "Account", "DP", "Piece Rate"],
            "Shipment Counts": ["Cash Count", "Account Count", "DP Counts", "Piece Rate Count"],
            "Delivery Data": ["Total DRS", "Delivered Shipments", "Return Shipments", "CNFD Shipments", "Pending Shipments", "RTO Delivery"],
        };
        
        const allowedLabels = clickableLabels[reportCategory] || [];
        
        return [
            {
                title: "",
                dataIndex: "label",
                key: "label",
                width: "60%",
                render: (label, record) => {
                    const isClickable = allowedLabels.includes(label);
                    
                    // if (record.label === "Total" || !isClickable) {
                    //     return <span>{label}</span>;
                    // }
                    
                    // return (
                    //     <span
                    //         onClick={() => handleTableRowClick(record, reportCategory, dataType)}
                    //         style={{
                    //             cursor: "pointer",
                    //             color: "#1890ff",
                    //             textDecoration: "none",
                    //         }}
                    //     >
                    //         {label}
                    //     </span>
                    // );
                    return <span>{label}</span>;
                },
            },
            {
                title: "",
                dataIndex: "value",
                key: "value",
                width: "40%",
                align: "right",
            },
        ];
    };

    return (
        <div className="director-dashboard">

            {/* KPI Cards Section */}
            <Row gutter={[16, 16]} className="kpi-cards-row" style={{ display: "flex", flexWrap: "nowrap" }}>
                <Col flex={1}>
                    <Card className="kpi-card kpi-card-red">
                        <Spin spinning={kpiDataLoading}>
                            <div className="kpi-card-content">
                                <div className="kpi-card-icon">
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                        <path
                                            d="M12 2L2 7L12 12L22 7L12 2Z"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M2 17L12 22L22 17"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M2 12L12 17L22 12"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </div>
                                <div className="kpi-card-info">
                                    <div className="kpi-card-title">TODAY HUB INSCAN SHIPMENTS</div>
                                    <div className="kpi-card-value">{formatIndianNumber(kpiData.todayHubInscanShipments)}</div>
                                </div>
                                <div className="kpi-card-refresh" onClick={handleRefreshHubOps}>
                                    <ReloadOutlined />
                                </div>
                            </div>
                        </Spin>
                    </Card>
                </Col>
                <Col flex={1}>
                    <Card className="kpi-card kpi-card-green">
                        <Spin spinning={kpiDataLoading}>
                            <div className="kpi-card-content">
                                <div className="kpi-card-icon">
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                        <path
                                            d="M1 3H5L7.68 14.39C7.77144 14.8504 8.02191 15.264 8.38755 15.5583C8.75318 15.8526 9.2107 16.009 9.68 16H19M9 16H20L23 6H7"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <circle cx="10" cy="20" r="1" fill="currentColor" />
                                        <circle cx="20" cy="20" r="1" fill="currentColor" />
                                    </svg>
                                </div>
                                <div className="kpi-card-info">
                                    <div className="kpi-card-title">TODAY HUB OUTSCAN SHIPMENTS</div>
                                    <div className="kpi-card-value">{formatIndianNumber(kpiData.todayHubOutscanShipments)}</div>
                                </div>
                                <div className="kpi-card-refresh" onClick={handleRefreshHubOps}>
                                    <ReloadOutlined />
                                </div>
                            </div>
                        </Spin>
                    </Card>
                </Col>
                <Col flex={1}>
                    <Card className="kpi-card kpi-card-blue">
                        <Spin spinning={kpiDataLoading}>
                            <div className="kpi-card-content">
                                <div className="kpi-card-icon">
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                        <path
                                            d="M4 7H20L19 21H5L4 7Z"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M8 7V4C8 2.89543 8.89543 2 10 2H14C15.1046 2 16 2.89543 16 4V7"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M12 11V17"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </div>
                                <div className="kpi-card-info">
                                    <div className="kpi-card-title">TODAY HUB INSCAN BAGS</div>
                                    <div className="kpi-card-value">{formatIndianNumber(kpiData.todayHubInscanBags)}</div>
                                </div>
                                <div className="kpi-card-refresh" onClick={handleRefreshHubOps}>
                                    <ReloadOutlined />
                                </div>
                            </div>
                        </Spin>
                    </Card>
                </Col>

                

                <Col flex={1}>
                    <Card className="kpi-card kpi-card-red">
                        <Spin spinning={kpiDataLoading}>
                            <div className="kpi-card-content">
                                <div className="kpi-card-icon">
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                        <path
                                            d="M4 7H20L19 21H5L4 7Z"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M8 7V4C8 2.89543 8.89543 2 10 2H14C15.1046 2 16 2.89543 16 4V7"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </div>
                                <div className="kpi-card-info">
                                    <div className="kpi-card-title">TODAY HUB OUTSCAN BAGS</div>
                                    <div className="kpi-card-value">{formatIndianNumber(kpiData.todayHubOutscanBags)}</div>
                                </div>
                                <div className="kpi-card-refresh" onClick={handleRefreshHubOps}>
                                    <ReloadOutlined />
                                </div>
                            </div>
                        </Spin>
                    </Card>
                </Col>

                <Col flex={1}>
                    <Card className="kpi-card kpi-card-light-blue">
                        <Spin spinning={kpiDataLoading}>
                            <div className="kpi-card-content">
                                <div className="kpi-card-icon">
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                        <path
                                            d="M4 7H20L19 21H5L4 7Z"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M8 7V4C8 2.89543 8.89543 2 10 2H14C15.1046 2 16 2.89543 16 4V7"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M4 7H20"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </div>
                                <div className="kpi-card-info">
                                    <div className="kpi-card-title">TODAY HUB FINALIZE BAGS</div>
                                    <div className="kpi-card-value">{formatIndianNumber(kpiData.todayHubFinalizeBags)}</div>
                                </div>
                                <div className="kpi-card-refresh" onClick={handleRefreshHubOps}>
                                    <ReloadOutlined />
                                </div>
                            </div>
                        </Spin>
                    </Card>
                </Col>
            </Row>


            {/* Data Tables Section */}
            <Row gutter={[12, 12]} className="data-tables-row">
                {/* Business Data Column */}
                <Col xs={24} md={8}>
                    <Card
                        className="data-table-card"
                        title={
                            <div className="data-table-header">
                                <span>Business Data - Previous Day</span>
                                <a onClick={(e) => handleViewZoneWise(e, "today", "Piece Rate", "Business Data")} className="view-zone-wise-link">
                                    View Zone Wise
                                </a>
                            </div>
                        }
                        extra={
                            <div className="data-table-refresh" onClick={handleRefreshBusinessData}>
                                <ReloadOutlined />
                            </div>
                        }
                    >
                        <Table
                            columns={getClickableLabelColumns("Business Data", "today")}
                            dataSource={businessDataToday}
                            pagination={false}
                            loading={businessDataLoading}
                            size="small"
                            className="data-table"
                            scroll={{ y: 220 }}
                            showHeader={false}
                        />
                    </Card>

                    <Card
                        className="data-table-card"
                        title={
                            <div className="data-table-header">
                                <span>Business Data - Current Month</span>
                                <a onClick={(e) => handleViewZoneWise(e, "range", "Piece Rate", "Business Data")} className="view-zone-wise-link">
                                    View Zone Wise
                                </a>
                            </div>
                        }
                        extra={
                            <div className="data-table-refresh" onClick={handleRefreshBusinessData}>
                                <ReloadOutlined />
                            </div>
                        }
                    >
                        <Table
                            columns={dataTableColumns}
                            dataSource={businessDataRange}
                            pagination={false}
                            loading={businessDataLoading}
                            size="small"
                            className="data-table"
                            showHeader={false}
                        />
                    </Card>
                </Col>

                {/* Shipment Counts Column */}
                <Col xs={24} md={8}>
                    <Card
                        className="data-table-card"
                        title={
                            <div className="data-table-header">
                                <span>Shipment Counts - Previous Day</span>
                                <a onClick={(e) => handleViewZoneWise(e, "today", "Piece Rate", "Shipment Counts")} className="view-zone-wise-link">
                                    View Zone Wise
                                </a>
                            </div>
                        }
                        extra={
                            <div className="data-table-refresh" onClick={handleRefreshShipmentCounts}>
                                <ReloadOutlined />
                            </div>
                        }
                    >
                        <Table
                            columns={getClickableLabelColumns("Shipment Counts", "today")}
                            dataSource={shipmentCountsToday}
                            pagination={false}
                            loading={shipmentCountsLoading}
                            size="small"
                            className="data-table"
                            showHeader={false}
                        />
                    </Card>

                    <Card
                        className="data-table-card"
                        title={
                            <div className="data-table-header">
                                <span>Shipment Counts - Current Month</span>
                                <a onClick={(e) => handleViewZoneWise(e, "range", "Piece Rate", "Shipment Counts")} className="view-zone-wise-link">
                                    View Zone Wise
                                </a>
                            </div>
                        }
                        extra={
                            <div className="data-table-refresh" onClick={handleRefreshShipmentCounts}>
                                <ReloadOutlined />
                            </div>
                        }
                    >
                        <Table
                            columns={dataTableColumns}
                            dataSource={shipmentCountsRange}
                            pagination={false}
                            loading={shipmentCountsLoading}
                            size="small"
                            className="data-table"
                            showHeader={false}
                        />
                    </Card>
                </Col>

                {/* Delivery Data Column */}
                <Col xs={24} md={8}>
                    <Card
                        className="data-table-card"
                        style={{ maxHeight: "360px" }}
                        title={
                            <div className="data-table-header">
                                <span>Delivery Data - Previous Day</span>
                                <a onClick={(e) => handleViewZoneWise(e, "today", "Piece Rate", "Delivery Data")} className="view-zone-wise-link">
                                    View Zone Wise
                                </a>
                            </div>
                        }
                        extra={
                            <div className="data-table-refresh" style={{ marginLeft: "10px" }} onClick={handleRefreshDeliveryData}>
                                <ReloadOutlined />
                            </div>
                        }
                    >
                        <Table
                            columns={getClickableLabelColumns("Delivery Data", "today")}
                            dataSource={deliveryDataToday}
                            pagination={false}
                            loading={deliveryDataLoading}
                            size="small"
                            className="data-table"
                            showHeader={false}
                            // scroll={{ y: 400 }}
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default DirectorDashboard2;

